import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Department, Team, SurveyRespondent, SurveyTemplate } from '@/types/database';
import { Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import { SurveyHeader } from './SurveyHeader';
import { RespondentFilters } from './RespondentFilters';
import { RespondentTable } from './RespondentTable';
import { RespondentModal } from './RespondentModal';
import { sendSurveyEmail } from '@/lib/email';

interface RespondentManagerProps {
  surveyId: string;
}

export function RespondentManager({ surveyId }: RespondentManagerProps) {
  const { organizationId } = useOrganization();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [respondents, setRespondents] = useState<SurveyRespondent[]>([]);
  const [surveyTemplate, setSurveyTemplate] = useState<SurveyTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [editingRespondent, setEditingRespondent] = useState<SurveyRespondent | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    departmentId: '',
    teamId: ''
  });

  useEffect(() => {
    loadData();
  }, [organizationId, surveyId]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [templateData, deptData, teamData, respData] = await Promise.all([
        supabase
          .from('survey_templates')
          .select('*')
          .eq('id', surveyId)
          .single(),
        supabase
          .from('departments')
          .select('*')
          .eq('organization_id', organizationId)
          .order('name'),
        supabase
          .from('teams')
          .select('*')
          .order('name'),
        supabase
          .from('survey_respondents')
          .select('*')
          .eq('organization_id', organizationId)
          .order('last_name')
      ]);

      if (templateData.error) throw templateData.error;
      if (deptData.error) throw deptData.error;
      if (teamData.error) throw teamData.error;
      if (respData.error) throw respData.error;

      setSurveyTemplate(templateData.data);
      setDepartments(deptData.data || []);
      setTeams(teamData.data || []);
      setRespondents(respData.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const text = await file.text();
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const { data, errors } = results;
            if (errors.length > 0) {
              throw new Error('CSV parsing failed');
            }

            const respondents = data.map((row: any) => ({
              organization_id: organizationId,
              first_name: row.firstName?.trim(),
              last_name: row.lastName?.trim(),
              email: row.email?.trim().toLowerCase(),
              phone_number: row.phoneNumber?.trim() || null,
              department_id: row.departmentName 
                ? departments.find(d => d.name === row.departmentName)?.id 
                : null,
              team_id: row.teamName && row.departmentName
                ? teams.find(t => 
                    t.name === row.teamName && 
                    t.department_id === departments.find(d => d.name === row.departmentName)?.id
                  )?.id
                : null
            }));

            const { error: insertError } = await supabase
              .from('survey_respondents')
              .upsert(respondents, {
                onConflict: 'organization_id,email',
                ignoreDuplicates: false
              });

            if (insertError) throw insertError;

            await loadData();
            setSuccess('Respondents uploaded successfully');
          } catch (err) {
            console.error('Error processing CSV:', err);
            setError('Failed to process CSV file');
          }
        }
      });
    } catch (err) {
      console.error('Error reading file:', err);
      setError('Failed to read CSV file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const data = {
        organization_id: organizationId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email.toLowerCase(),
        phone_number: formData.phoneNumber || null,
        department_id: formData.departmentId || null,
        team_id: formData.teamId || null
      };

      if (editingRespondent) {
        const { error } = await supabase
          .from('survey_respondents')
          .update(data)
          .eq('id', editingRespondent.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('survey_respondents')
          .insert([data]);

        if (error) throw error;
      }

      await loadData();
      setShowAddModal(false);
      setEditingRespondent(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        departmentId: '',
        teamId: ''
      });
      setSuccess(editingRespondent ? 'Respondent updated successfully' : 'Respondent added successfully');
    } catch (err) {
      console.error('Error saving respondent:', err);
      setError('Failed to save respondent');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this respondent?')) return;

    try {
      const { error } = await supabase
        .from('survey_respondents')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadData();
      setSuccess('Respondent deleted successfully');
    } catch (err) {
      console.error('Error deleting respondent:', err);
      setError('Failed to delete respondent');
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'firstName,lastName,email,phoneNumber,departmentName,teamName\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'respondents_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    );
  }

  const filteredRespondents = respondents.filter(respondent => {
    if (selectedDepartment && respondent.department_id !== selectedDepartment) return false;
    if (selectedTeam && respondent.team_id !== selectedTeam) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <SurveyHeader
        template={surveyTemplate}
        uploading={uploading}
        onDownloadTemplate={downloadTemplate}
        onUpload={() => fileInputRef.current?.click()}
        onAddRespondent={() => {
          setEditingRespondent(null);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            departmentId: '',
            teamId: ''
          });
          setShowAddModal(true);
        }}
        fileInputRef={fileInputRef}
      />

      <RespondentFilters
        departments={departments}
        teams={teams}
        selectedDepartment={selectedDepartment}
        selectedTeam={selectedTeam}
        onDepartmentChange={(departmentId) => {
          setSelectedDepartment(departmentId);
          setSelectedTeam(null);
        }}
        onTeamChange={setSelectedTeam}
      />

      <RespondentTable
        respondents={filteredRespondents}
        departments={departments}
        teams={teams}
        surveyId={surveyId}
        onEdit={(respondent) => {
          setEditingRespondent(respondent);
          setFormData({
            firstName: respondent.first_name,
            lastName: respondent.last_name,
            email: respondent.email,
            phoneNumber: respondent.phone_number || '',
            departmentId: respondent.department_id || '',
            teamId: respondent.team_id || ''
          });
          setShowAddModal(true);
        }}
        onDelete={handleDelete}
      />

      <RespondentModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingRespondent(null);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            departmentId: '',
            teamId: ''
          });
        }}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        departments={departments}
        teams={teams}
        editingRespondent={editingRespondent}
      />
    </div>
  );
}